import unsupportedHtml from "./unsupported.html";
import unsupportedCss from "./unsupported.css";

/**
 * Unsupported features info
 */
type UnsupportedInfo = {
    /** Unsupported features */
    features: string[],

    /** Unsupported expressions */
    expressions: string[]
}

/**
 * Run unsupported browser detection logic. Use contentId to locate textarea with protected scripts. When browser is supported
 * those scripts are inserted as html elements and executed.
 */
const run = () => {
    var unsupportedInfo: UnsupportedInfo;

    // support testing using ubc-force-error and ubc-force-ok url tags
    if (window.location.href.indexOf("ubc-force-error") >= 0) {
        unsupportedInfo = {
            features: [ "TEST" ],
            expressions: []
        }
    } else if (window.location.href.indexOf("ubc-force-ok") >= 0) {
        unsupportedInfo = {
            features: [],
            expressions: []
        }
    } else if (!document.currentScript) {
        unsupportedInfo = {
            features: [ "currentScript" ],
            expressions: []
        }
    } else {
        unsupportedInfo = getUnsupportedFeatures();
    }

    if (unsupportedInfo.features.length > 0) {
        handleBrowserUnsupported(unsupportedInfo);
    } else {
        handleBrowserSupported();
    }
}

/**
 * Handles supported browser case
 * - scripts from textarea are included as script tags to dom and loaded
 */
const handleBrowserSupported = () => {
        const scriptEl = document.currentScript;
        if (!scriptEl)
            return;

        // get all scripts from textarea content
        const tempEl = document.createElement("div");
        const content = scriptEl?.getAttribute("data-content");
        if (content) {
            // decode base64 data
            tempEl.innerHTML = atob(content);
        }

        // scripts inserted via innerHtml would not execute - https://www.w3.org/TR/2008/WD-html5-20080610/dom.html#innerhtml0
        tempEl.querySelectorAll("script").forEach(scriptEl => {
            // clone element and copy all attributes
            var cloned = document.createElement("script");

            Array.from(scriptEl.attributes).forEach(attr => cloned.setAttribute(attr.name, attr.value));
            document.body.appendChild(cloned);
        });
}

/**
 * Handle unsupported browser detected case
 * - report unsupported features to AI and console
 * - show unsupported browser screen
 *
 * @param {UnsupportedInfo} unsupported Unsupported features info
 */
const handleBrowserUnsupported = (unsupported: UnsupportedInfo) => {
    // report unsupported features to console and maybe loaded AI
    if (typeof appInsights !== "undefined") {
        appInsights.trackEvent({
            name: "BrowserBlocked"
        }, {
            unsupportedFeatures: unsupported.features,
            unsupportedExpressions: unsupported.expressions
        }); // Send App insights event about browser being blocked
        appInsights.flush();
    }

    // write unsupported info
    if (unsupported.features.length > 0) {
        console.error("Unsupported features detected:", unsupported.features);
    }
    if (unsupported.expressions.length > 0) {
        console.error("Unsupported expressions detected:", unsupported.expressions);
    }

    // try to get logo url from data-logo attribute
    const logoUrl = document.currentScript?.getAttribute("data-logo") ?? "";

    // replace logo path
    var replacedCss = unsupportedCss.replace('__LOGO_URL__', logoUrl);

    // show unsupported screen
    document.body.innerHTML = unsupportedHtml;

    var styleEl = document.createElement('style');
    styleEl.innerHTML = replacedCss;
    document.body.appendChild(styleEl);
}

/**
 * Check unsupported features and return unsupported runtime list
 *
 * @returns Unsupported features info
 */
const getUnsupportedFeatures = (): UnsupportedInfo => {
    var unsupportedFeatures: string[] = []; // Browser is supported if it has Proxy class and can evaluate ES6 JavaScript
    var unsupportedExpressions: string[] = [];

    const checkIfUnsupported = (expressions: string[]) => {
        let isUnsupported = false;
        for (var i = 0; i < expressions.length; i++) {
            try {
                eval(expressions[i]);
            } catch (e) {
                unsupportedExpressions.push(expressions[i]);
                isUnsupported = true;
            }
        }
        return isUnsupported;
    }

    const es6Features = [ // Source: http://es6-features.org/#Constants
        "() => true",
        "() => { return true; }",
        "(x = 7) => x",
        "(...args) => args",
        "[1, 2, ...[3, 4]]",
        "`${1} + ${2}`",
        "0b111110111",
        "'𠮷'.codePointAt(0)",
        "/\s*/y",
        "var x = 1; var y = 2; const obj = {x, y}",
        "({ ['1' + '1']: 2 })",
        "({ foo(a, b) { return a + b; } })",
        "var [red, blue = 'blue', yellow] = ['red', 'blue', 'yellow']",
        "var { red = 1, blue: { green: green2 }, yellow } = { red: 1, blue: { green: 2 }, yellow: 3 }",
        "([red, blue]) => red + blue",
        "({ set lang(length) { this._lang = length } })",
        "({ get lang() { return 'cz'; } })",
        "for (let n of [1, 2, 3]) {}",
        "function* range() { yield 1; }",
        "async function* load() { yield await Promise.resolve(1); }",
        "var set = new Set(); set.add(1)",
        "var set = new WeakSet",
        "var set = new WeakMap()",
        "' '.repeat(4)",
        "'hello'.startsWith('ello', 1)",
        "'hello'.endsWith('ello')",
        "'hello'.includes('ello')",
        "isNaN(NaN)",
        "Number.isSafeInteger(42)",
        "Number.EPSILON.toString()",
        "Math.trunc(42.7)",
        "Math.sign(-1)",
        "new Promise(resolve => resolve())",
        "new Intl.Collator('de')",
        "new Intl.NumberFormat('en-US')",
        "new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })",
        "new Intl.DateTimeFormat('en-US')",
        "new Uint32Array()",
        "Object.assign({}, {}, {})",
        "[1].find(i => i === 1); [1].findIndex(i => i === 1);",
        "const i = 1",
        "let i = 1",
        "Symbol('3')",
        "class Demo { static property2 = 1; *bar() { yield 1; } } class Demo2 extends Demo {}"
    ];
    const es7Features = [ // Source: https://gist.github.com/rajaramtt/7df3702a04c644b0b62c9a64f48f3dbf#2-ecmascript-2016---es-7
        "2 ** 8",
        "async () => await true",
        "async function* nums() { yield 1; }",
        "Object.getOwnPropertyDescriptors({})",
        "Object.values({})",
        "Object.entries({})",
        "[7, 8].includes(2)",
        "class Demo { property = 0; }"
    ];
    const es8Features = [ // Source: https://gist.github.com/rajaramtt/7df3702a04c644b0b62c9a64f48f3dbf#2-ecmascript-2016---es-7
        "'1699'.padStart(7,0)",
        "[1, 2, 3,]"
    ];
    const es9Features = [ // Source: https://gist.github.com/rajaramtt/7df3702a04c644b0b62c9a64f48f3dbf#2-ecmascript-2016---es-7
        "new Promise((resolve) => resolve()).finally(() => {})",
        "(async () => { for await (let value of [1]) {} })()",
        "const { a, ...other } = { a: 5, b: 7, c: 8 }"
    ];
    const es10Features = [ // Source: https://gist.github.com/rajaramtt/7df3702a04c644b0b62c9a64f48f3dbf#2-ecmascript-2016---es-7
        "[[1], [2]].flat(2)",
        "[[1], [2]].flatMap(i => i)",
        "Object.fromEntries([['one', 1], ['two', 2], ['three', 3]])",
        "'   string    '.trimStart().trimEnd()",
        "Symbol('3').description.toString()",
        "try {} catch {}"
    ];
    const es11Features = [ // Source: https://gist.github.com/rajaramtt/7df3702a04c644b0b62c9a64f48f3dbf#2-ecmascript-2016---es-7
        "BigInt(5)",
        "({a: 5}?.b)",
        "true ?? false",
        "Promise.allSettled([])",
        "'str'.matchAll(/str/g)"
    ];
    const selectorFeatures = [
        "document.querySelector(':has(div)')",
    ];
    if (checkIfUnsupported(es6Features)) {
        unsupportedFeatures.push("ES6");
    }
    if (checkIfUnsupported(es7Features)) {
        unsupportedFeatures.push("ES7");
    }
    if (checkIfUnsupported(es8Features)) {
        unsupportedFeatures.push("ES8");
    }
    if (checkIfUnsupported(es9Features)) {
        unsupportedFeatures.push("ES9");
    }
    if (checkIfUnsupported(es10Features)) {
        unsupportedFeatures.push("ES10");
    }
    if (checkIfUnsupported(es11Features)) {
        unsupportedFeatures.push("ES11");
    }
    if (checkIfUnsupported(selectorFeatures)) {
        unsupportedFeatures.push("Selector");
    }
    if (typeof Proxy !== "function") { // Proxy class prototype either does not exist or is not a function.
        unsupportedFeatures.push("Proxy");
    }

    return {
        features: unsupportedFeatures,
        expressions: unsupportedExpressions
    };
}

run();